import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Loader2,
  AlertCircle,
  Users,
  Layers,
  CheckSquare,
  Square,
  RefreshCw,
  Info,
} from 'lucide-react';
import {
  apiService,
  LLMModel,
  Customer,
  TextToSpeechProvider,
  SpeechToTextProvider,
  AgentByModel,
  CustomerDefaultModel,
} from '../../services/api';

// ── Provider-grouped model entry ────────────────────────────────────────────

interface ProviderGroup {
  providerId: string;
  providerName: string;
  dataResidency?: string;
  models: LLMModel[];
}

function buildProviderGroups(models: LLMModel[]): ProviderGroup[] {
  const groupMap = new Map<string, ProviderGroup>();
  for (const model of models) {
    const key = model.provider?.id ?? model.providerId;
    const providerName =
      model.provider?.displayName ?? model.provider?.name ?? 'Unbekannter Provider';
    const dataResidency = model.provider?.dataResidency;
    const existing = groupMap.get(key);
    if (existing) {
      existing.models.push(model);
    } else {
      groupMap.set(key, { providerId: key, providerName, dataResidency, models: [model] });
    }
  }
  return [...groupMap.values()].sort((a, b) =>
    a.providerName.localeCompare(b.providerName, 'de'),
  );
}

// ── Grouped-Select helper (reused in B1 and B2) ──────────────────────────────

interface GroupedModelSelectProps {
  /** All available models to build groups from. */
  models: LLMModel[];
  /** Current value – encoded as `"providerName::modelKey"`. */
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  id?: string;
}

function GroupedModelSelect({
  models,
  value,
  onValueChange,
  placeholder = 'Modell wählen…',
  id,
}: GroupedModelSelectProps) {
  const groups = buildProviderGroups(models);
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id={id} className="max-w-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {groups.map((group) => (
          <SelectGroup key={group.providerId}>
            <SelectLabel className="flex items-center gap-2">
              {group.providerName}
              {group.dataResidency && (
                <span className="text-[10px] font-semibold uppercase rounded bg-blue-600 px-1 py-0.5 text-white">
                  {group.dataResidency}
                </span>
              )}
            </SelectLabel>
            {group.models.map((m) => (
              <SelectItem
                key={m.id}
                value={`${m.provider?.name ?? ''}::${m.modelKey}`}
              >
                {m.displayName}
                <span className="text-xs text-gray-400 ml-1">({m.modelKey})</span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Grouped model select that uses `model.id` as value (needed for B2 / bulkSetDefaultLlmModel). */
interface GroupedModelSelectByIdProps {
  models: LLMModel[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  id?: string;
}

function GroupedModelSelectById({
  models,
  value,
  onValueChange,
  placeholder = 'Modell wählen…',
  id,
}: GroupedModelSelectByIdProps) {
  const groups = buildProviderGroups(models);
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id={id} className="max-w-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {groups.map((group) => (
          <SelectGroup key={group.providerId}>
            <SelectLabel className="flex items-center gap-2">
              {group.providerName}
              {group.dataResidency && (
                <span className="text-[10px] font-semibold uppercase rounded bg-blue-600 px-1 py-0.5 text-white">
                  {group.dataResidency}
                </span>
              )}
            </SelectLabel>
            {group.models.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.displayName}
                <span className="text-xs text-gray-400 ml-1">({m.modelKey})</span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Page component ──────────────────────────────────────────────────────────

export function BulkModelActivationPage() {
  // --- Shared data state ---
  const [models, setModels] = useState<LLMModel[]>([]);
  const [ttsProviders, setTtsProviders] = useState<TextToSpeechProvider[]>([]);
  const [sttProviders, setSttProviders] = useState<SpeechToTextProvider[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Tab A: Customer target selection ---
  const [applyToAll, setApplyToAll] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(new Set());
  const [customerSearch, setCustomerSearch] = useState('');

  // --- Tab A: LLM sub-tab ---
  const [selectedModelIds, setSelectedModelIds] = useState<Set<string>>(new Set());
  const [defaultModelId, setDefaultModelId] = useState<string>('');
  const [submittingLlm, setSubmittingLlm] = useState(false);

  // --- Tab A: TTS sub-tab ---
  const [selectedTtsIds, setSelectedTtsIds] = useState<Set<string>>(new Set());
  const [submittingTts, setSubmittingTts] = useState(false);

  // --- Tab A: STT sub-tab ---
  const [selectedSttIds, setSelectedSttIds] = useState<Set<string>>(new Set());
  const [submittingSTT, setSubmittingSTT] = useState(false);

  // --- Tab B1: Agents nach Modell umstellen ---
  const [b1SourceModel, setB1SourceModel] = useState<string>(''); // "providerName::modelKey"
  const [b1AgentList, setB1AgentList] = useState<AgentByModel[]>([]);
  const [b1SearchLoading, setB1SearchLoading] = useState(false);
  const [b1Searched, setB1Searched] = useState(false);
  const [b1SelectedAgentIds, setB1SelectedAgentIds] = useState<Set<string>>(new Set());
  const [b1TargetModel, setB1TargetModel] = useState<string>(''); // "providerName::modelKey"
  const [b1Submitting, setB1Submitting] = useState(false);

  // --- Tab B2: Kunden-Default-Modell ändern ---
  const [b2Customers, setB2Customers] = useState<CustomerDefaultModel[]>([]);
  const [b2Loading, setB2Loading] = useState(false);
  const [b2SelectedIds, setB2SelectedIds] = useState<Set<string>>(new Set());
  const [b2Search, setB2Search] = useState('');
  const [b2TargetModelId, setB2TargetModelId] = useState<string>('');
  const [b2Submitting, setB2Submitting] = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [modelsData, ttsData, sttData] = await Promise.all([
        apiService.getLlmModels({ includeInactive: false }),
        apiService.getTextToSpeechProviders(),
        apiService.getSpeechToTextProviders(),
      ]);

      // Load all customers (limit=1000 to get everything in one shot)
      const customersData = await apiService.getCustomers({ limit: 1000 });

      setModels(modelsData);
      setTtsProviders(ttsData);
      setSttProviders(sttData);
      setCustomers(customersData.customers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadB2Customers = useCallback(async () => {
    try {
      setB2Loading(true);
      const data = await apiService.getCustomersWithDefaultModel();
      setB2Customers(data);
    } catch (err) {
      toast.error('Fehler beim Laden der Kunden-Defaults', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setB2Loading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // ── Tab A helpers ─────────────────────────────────────────────────────────

  const providerGroups = buildProviderGroups(models);

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()),
  );

  function toggleCustomer(id: string) {
    setSelectedCustomerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleModel(id: string) {
    setSelectedModelIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (defaultModelId === id) setDefaultModelId('');
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleGroupModels(group: ProviderGroup, allSelected: boolean) {
    setSelectedModelIds((prev) => {
      const next = new Set(prev);
      for (const m of group.models) {
        if (allSelected) next.delete(m.id);
        else next.add(m.id);
      }
      return next;
    });
  }

  function isGroupAllSelected(group: ProviderGroup): boolean {
    return group.models.every((m) => selectedModelIds.has(m.id));
  }

  function isGroupPartialSelected(group: ProviderGroup): boolean {
    return group.models.some((m) => selectedModelIds.has(m.id)) && !isGroupAllSelected(group);
  }

  function toggleTts(id: string) {
    setSelectedTtsIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleStt(id: string) {
    setSelectedSttIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function validateTargetSelection(): boolean {
    if (!applyToAll && selectedCustomerIds.size === 0) {
      toast.error('Zielkunden wählen', {
        description: 'Bitte "Alle Kunden" aktivieren oder mindestens einen Kunden auswählen.',
      });
      return false;
    }
    return true;
  }

  // ── Tab A submit handlers ─────────────────────────────────────────────────

  async function handleLlmSubmit() {
    if (!validateTargetSelection()) return;
    if (selectedModelIds.size === 0) {
      toast.error('Keine Modelle ausgewählt', {
        description: 'Bitte mindestens ein LLM-Modell auswählen.',
      });
      return;
    }

    setSubmittingLlm(true);
    try {
      const body = {
        applyToAll: applyToAll || undefined,
        customerIds: applyToAll ? undefined : [...selectedCustomerIds],
        modelIds: [...selectedModelIds],
      };

      const result = await apiService.bulkGrantLlmModels(body);

      if (defaultModelId) {
        await apiService.bulkSetDefaultLlmModel({
          applyToAll: applyToAll || undefined,
          customerIds: applyToAll ? undefined : [...selectedCustomerIds],
          modelId: defaultModelId,
        });
      }

      toast.success('Modelle aktiviert', {
        description: `${result.customersAffected.toString()} Kunden betroffen, ${result.recordsGranted.toString()} Zugriffe gewährt.`,
      });

      setSelectedModelIds(new Set());
      setDefaultModelId('');
    } catch (err) {
      toast.error('Fehler beim Aktivieren', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setSubmittingLlm(false);
    }
  }

  async function handleTtsSubmit() {
    if (!validateTargetSelection()) return;
    if (selectedTtsIds.size === 0) {
      toast.error('Keine Provider ausgewählt', {
        description: 'Bitte mindestens einen TTS-Provider auswählen.',
      });
      return;
    }

    setSubmittingTts(true);
    try {
      const result = await apiService.bulkAssignTtsProviders({
        applyToAll: applyToAll || undefined,
        customerIds: applyToAll ? undefined : [...selectedCustomerIds],
        providerIds: [...selectedTtsIds],
      });

      toast.success('TTS-Provider aktiviert', {
        description: `${result.customersAffected.toString()} Kunden betroffen, ${result.recordsGranted.toString()} Zuweisungen erstellt.`,
      });

      setSelectedTtsIds(new Set());
    } catch (err) {
      toast.error('Fehler beim Aktivieren', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setSubmittingTts(false);
    }
  }

  async function handleSttSubmit() {
    if (!validateTargetSelection()) return;
    if (selectedSttIds.size === 0) {
      toast.error('Keine Provider ausgewählt', {
        description: 'Bitte mindestens einen STT-Provider auswählen.',
      });
      return;
    }

    setSubmittingSTT(true);
    try {
      const result = await apiService.bulkAssignSttProviders({
        applyToAll: applyToAll || undefined,
        customerIds: applyToAll ? undefined : [...selectedCustomerIds],
        providerIds: [...selectedSttIds],
      });

      toast.success('STT-Provider aktiviert', {
        description: `${result.customersAffected.toString()} Kunden betroffen, ${result.recordsGranted.toString()} Zuweisungen erstellt.`,
      });

      setSelectedSttIds(new Set());
    } catch (err) {
      toast.error('Fehler beim Aktivieren', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setSubmittingSTT(false);
    }
  }

  // ── Tab B1 handlers ───────────────────────────────────────────────────────

  function parseModelKey(encoded: string): { providerName: string; modelKey: string } | null {
    const idx = encoded.indexOf('::');
    if (idx === -1) return null;
    return { providerName: encoded.slice(0, idx), modelKey: encoded.slice(idx + 2) };
  }

  async function handleB1Search() {
    const parsed = parseModelKey(b1SourceModel);
    if (!parsed) {
      toast.error('Kein Quell-Modell gewählt');
      return;
    }
    setB1SearchLoading(true);
    setB1Searched(false);
    setB1AgentList([]);
    setB1SelectedAgentIds(new Set());
    try {
      const agents = await apiService.getAgentsByModel(parsed.providerName, parsed.modelKey);
      setB1AgentList(agents);
      setB1Searched(true);
    } catch (err) {
      toast.error('Fehler beim Suchen', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setB1SearchLoading(false);
    }
  }

  function toggleB1Agent(id: string) {
    setB1SelectedAgentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleB1SelectAll() {
    if (b1SelectedAgentIds.size === b1AgentList.length && b1AgentList.length > 0) {
      setB1SelectedAgentIds(new Set());
    } else {
      setB1SelectedAgentIds(new Set(b1AgentList.map((a) => a.id)));
    }
  }

  async function handleB1Submit() {
    const parsed = parseModelKey(b1TargetModel);
    if (!parsed) {
      toast.error('Kein Ziel-Modell gewählt');
      return;
    }
    if (b1SelectedAgentIds.size === 0) {
      toast.error('Keine Agents ausgewählt');
      return;
    }

    setB1Submitting(true);
    try {
      const result = await apiService.bulkChangeAgentModel({
        agentIds: [...b1SelectedAgentIds],
        llmProvider: parsed.providerName,
        llmModel: parsed.modelKey,
      });

      toast.success('Modell umgestellt', {
        description: `${result.changed.toString()} Agents umgestellt, ${result.customersGranted.toString()} Kunden Modell freigeschaltet.`,
      });

      // Reload the agent list — changed agents will now be gone from the source filter
      await handleB1Search();
    } catch (err) {
      toast.error('Fehler beim Umstellen', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setB1Submitting(false);
    }
  }

  // ── Tab B2 handlers ───────────────────────────────────────────────────────

  function toggleB2Customer(id: string) {
    setB2SelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const b2Filtered = b2Customers.filter((c) =>
    c.customerName.toLowerCase().includes(b2Search.toLowerCase()),
  );

  function toggleB2SelectAll() {
    if (b2SelectedIds.size === b2Filtered.length && b2Filtered.length > 0) {
      setB2SelectedIds(new Set());
    } else {
      setB2SelectedIds(new Set(b2Filtered.map((c) => c.customerId)));
    }
  }

  async function handleB2Submit() {
    if (b2SelectedIds.size === 0) {
      toast.error('Keine Kunden ausgewählt');
      return;
    }
    if (!b2TargetModelId) {
      toast.error('Kein Ziel-Modell gewählt');
      return;
    }

    setB2Submitting(true);
    try {
      const result = await apiService.bulkSetDefaultLlmModel({
        customerIds: [...b2SelectedIds],
        modelId: b2TargetModelId,
      });

      toast.success('Default-Modell gesetzt', {
        description: result.message,
      });

      setB2SelectedIds(new Set());
      setB2TargetModelId('');
      await loadB2Customers();
    } catch (err) {
      toast.error('Fehler beim Setzen des Default-Modells', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setB2Submitting(false);
    }
  }

  // ── Loading / Error states ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600">Lade Daten...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-red-800 font-medium mb-1">Fehler beim Laden der Daten</h3>
            <p className="text-red-600 text-sm">{error}</p>
            <button
              onClick={() => void loadData()}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const selectedModelsList = models.filter((m) => selectedModelIds.has(m.id));
  const b1AllSelected =
    b1AgentList.length > 0 && b1SelectedAgentIds.size === b1AgentList.length;
  const b1SomeSelected =
    b1SelectedAgentIds.size > 0 && b1SelectedAgentIds.size < b1AgentList.length;
  const b2AllSelected =
    b2Filtered.length > 0 && b2SelectedIds.size === b2Filtered.length;
  const b2SomeSelected =
    b2SelectedIds.size > 0 && b2SelectedIds.size < b2Filtered.length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Layers className="w-6 h-6 text-violet-600" />
          Modelle & Provider verwalten
        </h1>
        <p className="text-gray-600 mt-1">
          Aktiviere Modelle für Kunden, stelle Agents auf neue Modelle um oder passe Kunden-Defaults an.
        </p>
      </div>

      {/* ── Top-level tabs ── */}
      <Tabs defaultValue="activate">
        <TabsList>
          <TabsTrigger value="activate">Aktivieren</TabsTrigger>
          <TabsTrigger value="change">Umstellen &amp; Filtern</TabsTrigger>
        </TabsList>

        {/* ════════════════════════════════════════════════════════════════
            TAB A — Aktivieren (existing content, unchanged logic)
            ════════════════════════════════════════════════════════════════ */}
        <TabsContent value="activate" className="mt-4 space-y-6">

          {/* Customer target selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                Zielkunden
              </CardTitle>
              <CardDescription>
                Gilt für alle Tabs. Wähle Alle Kunden oder selektiere einzelne Kunden.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <Switch
                  id="apply-to-all"
                  checked={applyToAll}
                  onCheckedChange={(checked) => {
                    setApplyToAll(checked);
                    if (checked) setSelectedCustomerIds(new Set());
                  }}
                />
                <Label htmlFor="apply-to-all" className="font-medium">
                  Alle Kunden ({customers.length})
                </Label>
              </div>

              {!applyToAll && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-gray-700">
                      Kunden auswählen
                      {selectedCustomerIds.size > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {selectedCustomerIds.size} ausgewählt
                        </Badge>
                      )}
                    </Label>
                    {selectedCustomerIds.size > 0 && (
                      <button
                        onClick={() => { setSelectedCustomerIds(new Set()); }}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Auswahl aufheben
                      </button>
                    )}
                  </div>
                  <Input
                    placeholder="Kunden suchen..."
                    value={customerSearch}
                    onChange={(e) => { setCustomerSearch(e.target.value); }}
                    className="max-w-sm"
                  />
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {filteredCustomers.length === 0 ? (
                      <p className="text-sm text-gray-500 p-2">Keine Kunden gefunden.</p>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          className="flex items-center space-x-2 py-1 px-1 rounded hover:bg-gray-50"
                        >
                          <Checkbox
                            id={`customer-${customer.id}`}
                            checked={selectedCustomerIds.has(customer.id)}
                            onCheckedChange={() => { toggleCustomer(customer.id); }}
                          />
                          <Label
                            htmlFor={`customer-${customer.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {customer.name}
                            {!customer.isActive && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                Inaktiv
                              </Badge>
                            )}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Inner activation tabs */}
          <Tabs defaultValue="llm">
            <TabsList>
              <TabsTrigger value="llm">
                LLM-Modelle
                {selectedModelIds.size > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {selectedModelIds.size}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="tts">
                TTS-Provider
                {selectedTtsIds.size > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {selectedTtsIds.size}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="stt">
                STT-Provider
                {selectedSttIds.size > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {selectedSttIds.size}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ── LLM Sub-Tab ── */}
            <TabsContent value="llm" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">LLM-Modelle auswählen</CardTitle>
                  <CardDescription>
                    Modelle sind nach Provider gruppiert. Mit "Alle" kannst du alle Modelle eines
                    Providers auf einmal auswählen.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {providerGroups.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      Keine aktiven Modelle gefunden.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {providerGroups.map((group) => {
                        const allSelected = isGroupAllSelected(group);
                        const partialSelected = isGroupPartialSelected(group);
                        return (
                          <div
                            key={group.providerId}
                            className="overflow-hidden rounded-lg border border-gray-200"
                          >
                            {/* Provider header */}
                            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-semibold text-gray-800">
                                  {group.providerName}
                                </span>
                                {group.dataResidency && (
                                  <Badge className="text-xs font-semibold uppercase bg-blue-600 text-white border-transparent hover:bg-blue-600">
                                    {group.dataResidency}
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {group.models.length}
                                </Badge>
                              </div>
                              <button
                                type="button"
                                onClick={() => { toggleGroupModels(group, allSelected); }}
                                className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium"
                                aria-label={
                                  allSelected
                                    ? `Alle ${group.providerName}-Modelle abwählen`
                                    : `Alle ${group.providerName}-Modelle auswählen`
                                }
                              >
                                {allSelected || partialSelected ? (
                                  <CheckSquare className="w-4 h-4" />
                                ) : (
                                  <Square className="w-4 h-4" />
                                )}
                                Alle
                              </button>
                            </div>

                            {/* Models */}
                            <div className="p-3 space-y-1">
                              {group.models.map((model) => (
                                <div
                                  key={model.id}
                                  className="flex items-center space-x-2 py-1 px-1 rounded hover:bg-gray-50"
                                >
                                  <Checkbox
                                    id={`model-bulk-${model.id}`}
                                    checked={selectedModelIds.has(model.id)}
                                    onCheckedChange={() => { toggleModel(model.id); }}
                                  />
                                  <Label
                                    htmlFor={`model-bulk-${model.id}`}
                                    className="text-sm cursor-pointer flex-1"
                                  >
                                    {model.displayName}
                                    <span className="text-xs text-gray-500 ml-1">({model.modelKey})</span>
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Default model selection */}
              {selectedModelIds.size > 0 && (
                <Card className="border-violet-100 bg-violet-50/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-violet-800">
                      Standard-Modell setzen (optional)
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Wird als Default-Modell für die Zielkunden gesetzt. Kunden, die dieses Modell
                      nicht erhalten, behalten ihr bisheriges Default.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Select value={defaultModelId} onValueChange={setDefaultModelId}>
                      <SelectTrigger className="max-w-sm">
                        <SelectValue placeholder="Kein Standard-Modell (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {buildProviderGroups(selectedModelsList).map((group) => (
                          <SelectGroup key={group.providerId}>
                            <SelectLabel className="flex items-center gap-2">
                              {group.providerName}
                              {group.dataResidency && (
                                <span className="text-[10px] font-semibold uppercase rounded bg-blue-600 px-1 py-0.5 text-white">
                                  {group.dataResidency}
                                </span>
                              )}
                            </SelectLabel>
                            {group.models.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.displayName}
                                <span className="text-xs text-gray-400 ml-1">({m.modelKey})</span>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={() => void handleLlmSubmit()}
                  disabled={submittingLlm || selectedModelIds.size === 0}
                >
                  {submittingLlm && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Modelle aktivieren
                  {selectedModelIds.size > 0 && ` (${selectedModelIds.size.toString()})`}
                </Button>
              </div>
            </TabsContent>

            {/* ── TTS Sub-Tab ── */}
            <TabsContent value="tts" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">TTS-Provider auswählen</CardTitle>
                  <CardDescription>
                    Wähle die Text-to-Speech-Provider, die den Zielkunden freigeschaltet werden sollen.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {ttsProviders.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      Keine TTS-Provider gefunden.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {ttsProviders.map((provider) => (
                        <div
                          key={provider.id}
                          className="flex items-center space-x-2 py-1.5 px-2 rounded hover:bg-gray-50"
                        >
                          <Checkbox
                            id={`tts-bulk-${provider.id}`}
                            checked={selectedTtsIds.has(provider.id)}
                            onCheckedChange={() => { toggleTts(provider.id); }}
                          />
                          <Label
                            htmlFor={`tts-bulk-${provider.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {provider.displayName}
                            <span className="text-xs text-gray-400 ml-1">({provider.providerType})</span>
                          </Label>
                          <Badge
                            variant={provider.status === 'active' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {provider.status === 'active' ? 'Aktiv' : provider.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button
                  onClick={() => void handleTtsSubmit()}
                  disabled={submittingTts || selectedTtsIds.size === 0}
                >
                  {submittingTts && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  TTS-Provider aktivieren
                  {selectedTtsIds.size > 0 && ` (${selectedTtsIds.size.toString()})`}
                </Button>
              </div>
            </TabsContent>

            {/* ── STT Sub-Tab ── */}
            <TabsContent value="stt" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">STT-Provider auswählen</CardTitle>
                  <CardDescription>
                    Wähle die Speech-to-Text-Provider, die den Zielkunden freigeschaltet werden sollen.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {sttProviders.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      Keine STT-Provider gefunden.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {sttProviders.map((provider) => (
                        <div
                          key={provider.id}
                          className="flex items-center space-x-2 py-1.5 px-2 rounded hover:bg-gray-50"
                        >
                          <Checkbox
                            id={`stt-bulk-${provider.id}`}
                            checked={selectedSttIds.has(provider.id)}
                            onCheckedChange={() => { toggleStt(provider.id); }}
                          />
                          <Label
                            htmlFor={`stt-bulk-${provider.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {provider.displayName}
                            <span className="text-xs text-gray-400 ml-1">({provider.providerType})</span>
                          </Label>
                          <Badge
                            variant={provider.status === 'active' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {provider.status === 'active' ? 'Aktiv' : provider.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button
                  onClick={() => void handleSttSubmit()}
                  disabled={submittingSTT || selectedSttIds.size === 0}
                >
                  {submittingSTT && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  STT-Provider aktivieren
                  {selectedSttIds.size > 0 && ` (${selectedSttIds.size.toString()})`}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ════════════════════════════════════════════════════════════════
            TAB B — Umstellen & Filtern
            ════════════════════════════════════════════════════════════════ */}
        <TabsContent value="change" className="mt-4 space-y-6">

          {/* ── B1: Agents nach Modell umstellen ── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-violet-600" />
                Agents nach Modell umstellen
              </CardTitle>
              <CardDescription>
                Finde alle Agents, die ein bestimmtes Modell verwenden, und stelle sie auf ein anderes um.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Source model select + search button */}
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="b1-source-model" className="text-sm font-medium">
                    Quell-Modell
                  </Label>
                  <GroupedModelSelect
                    id="b1-source-model"
                    models={models}
                    value={b1SourceModel}
                    onValueChange={(v) => {
                      setB1SourceModel(v);
                      setB1Searched(false);
                      setB1AgentList([]);
                      setB1SelectedAgentIds(new Set());
                    }}
                    placeholder="Modell wählen…"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => void handleB1Search()}
                  disabled={!b1SourceModel || b1SearchLoading}
                >
                  {b1SearchLoading
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : null}
                  Agents suchen
                </Button>
              </div>

              {/* Agent table */}
              {b1Searched && (
                <>
                  {b1AgentList.length === 0 ? (
                    <p className="text-sm text-gray-500 py-3 text-center">
                      Keine Agents mit diesem Modell gefunden.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">{b1AgentList.length}</span> Agent{b1AgentList.length !== 1 ? 's' : ''} gefunden
                        {b1SelectedAgentIds.size > 0 && (
                          <Badge variant="secondary" className="ml-2">
                            {b1SelectedAgentIds.size} ausgewählt
                          </Badge>
                        )}
                      </p>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-3 py-2 w-8 text-left">
                                <Checkbox
                                  id="b1-select-all"
                                  checked={b1AllSelected}
                                  data-state={b1SomeSelected ? 'indeterminate' : b1AllSelected ? 'checked' : 'unchecked'}
                                  onCheckedChange={toggleB1SelectAll}
                                  aria-label="Alle Agents auswählen"
                                />
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Agent</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Kunde</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Aktuelles Modell</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {b1AgentList.map((agent) => (
                              <tr
                                key={agent.id}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => { toggleB1Agent(agent.id); }}
                              >
                                <td className="px-3 py-2">
                                  <Checkbox
                                    id={`b1-agent-${agent.id}`}
                                    checked={b1SelectedAgentIds.has(agent.id)}
                                    onCheckedChange={() => { toggleB1Agent(agent.id); }}
                                    onClick={(e) => { e.stopPropagation(); }}
                                  />
                                </td>
                                <td className="px-3 py-2 font-medium text-gray-900">
                                  {agent.name}
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {agent.customerName}
                                </td>
                                <td className="px-3 py-2 text-gray-500">
                                  <span className="font-mono text-xs">{agent.llmProvider}/{agent.llmModel}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Target model + submit */}
                  {b1AgentList.length > 0 && (
                    <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-gray-100">
                      <div className="space-y-1">
                        <Label htmlFor="b1-target-model" className="text-sm font-medium">
                          Ziel-Modell
                        </Label>
                        <GroupedModelSelect
                          id="b1-target-model"
                          models={models}
                          value={b1TargetModel}
                          onValueChange={setB1TargetModel}
                          placeholder="Ziel-Modell wählen…"
                        />
                      </div>
                      <Button
                        onClick={() => void handleB1Submit()}
                        disabled={
                          b1Submitting ||
                          b1SelectedAgentIds.size === 0 ||
                          !b1TargetModel
                        }
                      >
                        {b1Submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Modell umstellen
                        {b1SelectedAgentIds.size > 0 &&
                          ` (${b1SelectedAgentIds.size.toString()})`}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Hinweis B1 */}
          <div className="flex items-start gap-2 text-sm text-gray-500 -mt-2 px-1">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
            <span>Das Zielmodell wird betroffenen Kunden automatisch freigeschaltet.</span>
          </div>

          {/* ── B2: Kunden-Default-Modell ändern ── */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    Kunden-Default-Modell ändern
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Zeigt alle Kunden mit ihrem aktuellen Default-LLM. Wähle Kunden aus und setze ein neues Default.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void loadB2Customers()}
                  disabled={b2Loading}
                  aria-label="Kundenliste aktualisieren"
                >
                  {b2Loading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {b2Customers.length === 0 && !b2Loading ? (
                <div className="text-center py-6 space-y-3">
                  <p className="text-sm text-gray-500">
                    Noch keine Daten geladen.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadB2Customers()}
                  >
                    Jetzt laden
                  </Button>
                </div>
              ) : (
                <>
                  {/* Search */}
                  <Input
                    placeholder="Kunden suchen..."
                    value={b2Search}
                    onChange={(e) => { setB2Search(e.target.value); }}
                    className="max-w-sm"
                    aria-label="Kunden filtern"
                  />

                  {/* Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-3 py-2 w-8 text-left">
                            <Checkbox
                              id="b2-select-all"
                              checked={b2AllSelected}
                              data-state={b2SomeSelected ? 'indeterminate' : b2AllSelected ? 'checked' : 'unchecked'}
                              onCheckedChange={toggleB2SelectAll}
                              aria-label="Alle Kunden auswählen"
                            />
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Kunde</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">
                            Aktuelles Default-Modell
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {b2Loading ? (
                          <tr>
                            <td colSpan={3} className="px-3 py-6 text-center">
                              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                            </td>
                          </tr>
                        ) : b2Filtered.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="px-3 py-4 text-center text-gray-500 text-sm">
                              Keine Kunden gefunden.
                            </td>
                          </tr>
                        ) : (
                          b2Filtered.map((c) => (
                            <tr
                              key={c.customerId}
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => { toggleB2Customer(c.customerId); }}
                            >
                              <td className="px-3 py-2">
                                <Checkbox
                                  id={`b2-customer-${c.customerId}`}
                                  checked={b2SelectedIds.has(c.customerId)}
                                  onCheckedChange={() => { toggleB2Customer(c.customerId); }}
                                  onClick={(e) => { e.stopPropagation(); }}
                                />
                              </td>
                              <td className="px-3 py-2 font-medium text-gray-900">
                                {c.customerName}
                              </td>
                              <td className="px-3 py-2">
                                {c.defaultModelDisplayName ?? c.defaultModelKey ? (
                                  <span>
                                    <span className="text-gray-900">
                                      {c.defaultModelDisplayName ?? c.defaultModelKey}
                                    </span>
                                    {c.providerName && (
                                      <span className="ml-1.5 text-xs text-gray-400">
                                        {c.providerName}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 italic text-xs">— kein Default —</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* New default + submit */}
                  <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-gray-100">
                    <div className="space-y-1">
                      <Label htmlFor="b2-target-model" className="text-sm font-medium">
                        Neues Default-Modell
                        {b2SelectedIds.size > 0 && (
                          <Badge variant="secondary" className="ml-2">
                            {b2SelectedIds.size} Kunden
                          </Badge>
                        )}
                      </Label>
                      <GroupedModelSelectById
                        id="b2-target-model"
                        models={models}
                        value={b2TargetModelId}
                        onValueChange={setB2TargetModelId}
                        placeholder="Modell wählen…"
                      />
                    </div>
                    <Button
                      onClick={() => void handleB2Submit()}
                      disabled={b2Submitting || b2SelectedIds.size === 0 || !b2TargetModelId}
                    >
                      {b2Submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Default setzen
                      {b2SelectedIds.size > 0 && ` (${b2SelectedIds.size.toString()})`}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Hinweis B2 */}
          <div className="flex items-start gap-2 text-sm text-gray-500 -mt-2 px-1">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
            <span>Neue Agents dieser Kunden verwenden künftig das gewählte Modell als Default.</span>
          </div>

        </TabsContent>
      </Tabs>
    </div>
  );
}
